import React from "react";
import { ResponsiveContainer, LineChart, Line, BarChart, Bar, XAxis, Tooltip, PieChart, Pie, Cell, AreaChart, Area } from "recharts";

const GOLD = "#D4AF37";
export const PLATFORM_COLORS = { Instagram: "#E1306C", Facebook: "#1877F2", TikTok: "#25F4EE", X: "#e5e7eb", YouTube: "#FF0000" };
const PIE = ["#D4AF37", "#10B981", "#3B82F6", "#F59E0B", "#E11D48", "#8B5CF6"];

export function Sparkline({ data, dataKey = "value", color = GOLD, height = 40 }) {
  if (!data || data.length === 0) return null;
  return (
    <ResponsiveContainer width="100%" height={height}>
      <AreaChart data={data} margin={{ top: 2, right: 0, left: 0, bottom: 0 }}>
        <defs>
          <linearGradient id={`sp-${dataKey}-${color}`} x1="0" y1="0" x2="0" y2="1">
            <stop offset="0%" stopColor={color} stopOpacity={0.5} />
            <stop offset="100%" stopColor={color} stopOpacity={0} />
          </linearGradient>
        </defs>
        <Area type="monotone" dataKey={dataKey} stroke={color} strokeWidth={2} fill={`url(#sp-${dataKey}-${color})`} dot={false} />
      </AreaChart>
    </ResponsiveContainer>
  );
}

export function MiniLine({ data, dataKey = "revenue", color = GOLD, height = 200 }) {
  return (
    <ResponsiveContainer width="100%" height={height}>
      <LineChart data={data} margin={{ top: 8, right: 8, left: 0, bottom: 0 }}>
        <XAxis dataKey="date" tick={{ fill: "#777", fontSize: 11 }} axisLine={false} tickLine={false} />
        <Tooltip contentStyle={{ background: "#141414", border: "1px solid #262626", borderRadius: 8, color: "#fff" }} />
        <Line type="monotone" dataKey={dataKey} stroke={color} strokeWidth={2.5} dot={{ r: 3, fill: color }} />
      </LineChart>
    </ResponsiveContainer>
  );
}

export function MiniBar({ data, dataKey = "activity", color = GOLD, xKey = "date", height = 200 }) {
  return (
    <ResponsiveContainer width="100%" height={height}>
      <BarChart data={data} margin={{ top: 8, right: 8, left: 0, bottom: 0 }}>
        <XAxis dataKey={xKey} tick={{ fill: "#777", fontSize: 11 }} axisLine={false} tickLine={false} />
        <Tooltip cursor={{ fill: "#ffffff08" }} contentStyle={{ background: "#141414", border: "1px solid #262626", borderRadius: 8, color: "#fff" }} />
        <Bar dataKey={dataKey} fill={color} radius={[4, 4, 0, 0]} />
      </BarChart>
    </ResponsiveContainer>
  );
}

export function Donut({ data, nameKey = "platform", dataKey = "value", height = 200 }) {
  if (!data || data.length === 0) return null;
  return (
    <ResponsiveContainer width="100%" height={height}>
      <PieChart>
        <Pie data={data} nameKey={nameKey} dataKey={dataKey} innerRadius={55} outerRadius={85} paddingAngle={2} stroke="none">
          {data.map((d, i) => <Cell key={i} fill={PLATFORM_COLORS[d[nameKey]] || PIE[i % PIE.length]} />)}
        </Pie>
        <Tooltip contentStyle={{ background: "#141414", border: "1px solid #262626", borderRadius: 8, color: "#fff" }} />
      </PieChart>
    </ResponsiveContainer>
  );
}

export function ProgressBar({ value = 0, goal = 0, color = GOLD }) {
  const pct = goal > 0 ? Math.min(100, (value / goal) * 100) : 0;
  return (
    <div>
      <div className="h-3 rounded-full bg-edge overflow-hidden">
        <div className="h-full rounded-full transition-all duration-700" style={{ width: `${pct}%`, background: color }} />
      </div>
      <div className="flex justify-between mt-1.5 text-xs font-num text-[#A3A3A3]">
        <span>{Math.round(pct)}%</span>
        {goal > 0 && <span>${Math.max(0, goal - value).toLocaleString()} remaining</span>}
      </div>
    </div>
  );
}

export function ScoreRing({ score = 0, size = 120 }) {
  const r = size / 2 - 10;
  const c = 2 * Math.PI * r;
  const off = c - (score / 100) * c;
  const color = score >= 75 ? "#10B981" : score >= 50 ? GOLD : "#E11D48";
  return (
    <svg width={size} height={size} className="shrink-0">
      <circle cx={size / 2} cy={size / 2} r={r} stroke="#262626" strokeWidth="8" fill="none" />
      <circle cx={size / 2} cy={size / 2} r={r} stroke={color} strokeWidth="8" fill="none" strokeLinecap="round"
        strokeDasharray={c} strokeDashoffset={off} transform={`rotate(-90 ${size / 2} ${size / 2})`} style={{ transition: "stroke-dashoffset 1s ease" }} />
      <text x="50%" y="48%" textAnchor="middle" fill="#fff" fontSize="26" fontWeight="800" fontFamily="JetBrains Mono">{score}</text>
      <text x="50%" y="64%" textAnchor="middle" fill="#777" fontSize="10">/ 100</text>
    </svg>
  );
}

export function Funnel({ steps }) {
  const max = Math.max(...steps.map((s) => s.value), 1);
  return (
    <div className="space-y-2">
      {steps.map((s, i) => {
        const pct = (s.value / max) * 100;
        const conv = i > 0 && steps[i - 1].value > 0 ? Math.round((s.value / steps[i - 1].value) * 100) : null;
        return (
          <div key={s.label}>
            <div className="flex justify-between text-xs mb-1"><span className="label-caps">{s.label}</span><span className="font-num text-[#A3A3A3]">{s.value}{conv != null ? ` · ${conv}%` : ""}</span></div>
            <div className="h-6 rounded bg-edge overflow-hidden"><div className="h-full rounded" style={{ width: `${Math.max(6, pct)}%`, background: `linear-gradient(90deg, ${GOLD}, ${GOLD}99)` }} /></div>
          </div>
        );
      })}
    </div>
  );
}
